import { useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { INSTANCE_SLUGS } from '../instances/instanceSlugs';
import type { RootState } from '../store/store';
import type { User } from '../types/User';

/**
 * mcgraw-hill (ORA-1268): the plain Ora view, a per-browser demo switch a
 * McGraw Hill member toggles with Shift+D. While it is on, the app resolves
 * the member's instance to Ora's default one (`resolveProfileInstance`), so
 * every McGraw Hill gate, capability and brand goes off at once, and every
 * chat turn asks the backend for plain Ora (`mcgrawHillView: 'default'`).
 * Off by default: a member sees McGraw Hill until the presenter flips it.
 *
 * Two questions, kept apart: "is this a member" (the profile's real
 * instance: the hotkey, the chat flag) and "is McGraw Hill showing" (member
 * and not in the plain view: `useIsMcGrawHill` and everything visual).
 */
const STORAGE_KEY = 'mcgraw-hill-plain-ora-view';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
};

// Read from storage on first use, then kept here; undefined until read.
let current: boolean | undefined;
const listeners = new Set<() => void>();

/** The stored flag alone, with no membership check. */
export const isPlainOraViewOn = (): boolean => {
  if (current === undefined) current = readStored();
  return current;
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setPlainOraView = (next: boolean): void => {
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, 'on');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Not remembered past this tab; the change still applies.
  }

  if (isPlainOraViewOn() === next) return;
  current = next;
  listeners.forEach((listener) => listener());
};

/** Re-renders the caller when the flag changes; returns the stored flag. */
export const usePlainOraViewFlag = (): boolean =>
  useSyncExternalStore(subscribe, isPlainOraViewOn, isPlainOraViewOn);

/** Whether the profile's real instance (the backend's answer) is McGraw Hill. */
export const isMcGrawHillMemberProfile = (
  profile: Pick<User, 'instance'> | null | undefined
): boolean => profile?.instance?.slug === INSTANCE_SLUGS.mcgrawHill;

/**
 * Whether an instance with this real slug shows as Ora's default one: a
 * McGraw Hill member in the plain view. Any other instance never does.
 */
export const isPlainOraViewOver = (realSlug: string | undefined): boolean =>
  realSlug === INSTANCE_SLUGS.mcgrawHill && isPlainOraViewOn();

/**
 * Whether the signed-in user really belongs to McGraw Hill, whatever the
 * plain view shows. For the hotkey and the chat flag only; everything the
 * member sees reads `useIsMcGrawHill`.
 */
export const useIsMcGrawHillMember = (): boolean =>
  useSelector((state: Partial<RootState>) =>
    isMcGrawHillMemberProfile(state.user?.profile)
  );

/**
 * The chat turn's body for this profile: a member in the plain view asks for
 * plain Ora (the backend honours it only to turn McGraw Hill off for a
 * verified member). Anyone else's body is returned untouched.
 */
export const withPlainOraViewTurnFlag = (
  body: BodyInit | null | undefined,
  profile: Pick<User, 'instance'> | null | undefined
): BodyInit | null | undefined => {
  if (!isMcGrawHillMemberProfile(profile) || !isPlainOraViewOn()) return body;
  if (typeof body !== 'string') return body;

  try {
    const parsed: unknown = JSON.parse(body);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return body;
    }
    return JSON.stringify({ ...parsed, mcgrawHillView: 'default' });
  } catch {
    return body;
  }
};

/** Forgets the in-memory flag so the next read goes to storage (tests). */
export const resetPlainOraView = (): void => {
  current = undefined;
};
